import { usePage } from "@inertiajs/react"
import {
  IconBook,
  IconBriefcase,
  IconCalendar,
  IconClock,
  IconCoin,
  IconDashboard,
  IconFileText,
  IconLayoutKanban,
  IconSettings,
  IconTrendingUp,
  IconUser,
} from "@tabler/icons-react"

import { type NavGroup, NavMain, NavSection } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"

const mainNavItems = [
  { title: "Dashboard", url: "/", icon: IconDashboard },
  { title: "Time Tracking", url: "/time_entries", icon: IconClock },
  { title: "Calendar", url: "/calendar", icon: IconCalendar },
  { title: "Library", url: "/books", icon: IconBook },
]

const personalGroup: NavGroup = {
  title: "Personal",
  icon: IconUser,
  items: [
    { title: "Profile", url: "/profile" },
    { title: "Time Off Requests", url: "/time_off_requests" },
  ],
}

const financeGroup: NavGroup = {
  title: "Finance",
  icon: IconCoin,
  items: [
    { title: "Invoices", url: "/invoices" },
    { title: "Bills", url: "/bills" },
    { title: "Exchange Rates", url: "/exchange_rates" },
    { title: "Revenue Dashboard", url: "/admin/revenue_dashboard" },
    { title: "Project P&L", url: "/admin/project_finance_reports" },
  ],
}

const growthGroup: NavGroup = {
  title: "Growth & Career",
  icon: IconTrendingUp,
  items: [
    { title: "Career Pathway", url: "/growth/career-pathway" },
    { title: "Performance Lifecycle", url: "/growth/performance-lifecycle" },
    { title: "Compensations", url: "/admin/seniority_levels" },
  ],
}

const adminGroup: NavGroup = {
  title: "Admin",
  icon: IconSettings,
  items: [
    { title: "Users & Balances", url: "/admin/users" },
    { title: "All Requests", url: "/admin/time_off_requests" },
    { title: "Time Tracking", url: "/admin/time_tracking" },
    { title: "Feature Flags", url: "/admin/feature_flags" },
  ],
}

interface SharedProps {
  [key: string]: unknown
  auth: {
    user: {
      id: number
      name: string
      email: string
      role: string
      avatar_url: string | null
    }
    impersonating: boolean
    is_admin: boolean
  }
  feature_flags: Record<string, boolean>
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { auth, feature_flags } = usePage<SharedProps>().props

  const primaryNavItems = [
    ...mainNavItems,
    ...(feature_flags?.documents ? [{ title: "Documents", url: "/documents", icon: IconFileText }] : []),
    ...(feature_flags?.boards ? [{ title: "Boards", url: "/boards", icon: IconLayoutKanban }] : []),
  ]

  const operationsGroup: NavGroup = {
    title: "Operations",
    icon: IconBriefcase,
    items: [
      { title: "Customers", url: "/customers" },
      { title: "Projects", url: "/projects" },
      ...(auth.is_admin ? [{ title: "Legal Documents", url: "/legal_documents" }] : []),
    ],
  }

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:!p-1.5">
              <a href="/">
                <div className="bg-primary text-primary-foreground flex h-5 w-5 items-center justify-center rounded-md text-xs font-bold">
                  OC
                </div>
                <span className="text-base font-semibold">Ops Center</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={primaryNavItems} />
        <NavSection group={personalGroup} />
        <NavSection group={operationsGroup} />
        {auth.is_admin && <NavSection group={financeGroup} />}
        <NavSection group={growthGroup} />
        {auth.is_admin && <NavSection group={adminGroup} />}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  )
}
